import React, { Suspense, lazy } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, useParams, Navigate } from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthContext";
import { ProfileCompletionDialog } from "@/components/ProfileCompletionDialog";
import { NotificationProvider } from "@/contexts/NotificationContext";
import { AnalyticsProvider } from "@/components/AnalyticsProvider";
import { ScrollToTop } from "@/components/ScrollToTop";
import { ThemeTransition } from "@/components/ThemeTransition";
import { NotificationCenter } from "@/components/NotificationCenter";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { AdminRoute } from "@/components/AdminRoute";
import { ChatWidget } from "@/components/ChatWidget";
import { AgreementGate } from "@/components/AgreementGate";
import { MobileBottomNav } from "@/components/MobileBottomNav";
import SnowEffect from "@/components/SnowEffect";
import NewYearPopup from "@/components/NewYearPopup";
import { PwaInstallPrompt } from "@/components/PwaInstallPrompt";
import { PushNotificationPrompt } from "@/components/PushNotificationPrompt";

// Eager load Index for fast initial render
import Index from "./pages/Index";

// Lazy load all other pages
const Auth = lazy(() => import("./pages/Auth"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const Results = lazy(() => import("./pages/Results"));
const Kampanya = lazy(() => import("./pages/Kampanya"));
const Trend = lazy(() => import("./pages/Trend"));
const Repurpose = lazy(() => import("./pages/Repurpose"));
const Sablonlar = lazy(() => import("./pages/Sablonlar"));
const Influencers = lazy(() => import("./pages/Influencers"));
const VideoEditor = lazy(() => import("./pages/VideoEditor"));
const PaymentSuccess = lazy(() => import("./pages/PaymentSuccess"));
const PaymentFailed = lazy(() => import("./pages/PaymentFailed"));
const PaymentHistory = lazy(() => import("./pages/PaymentHistory"));
const Pricing = lazy(() => import("./pages/Pricing"));
const Referral = lazy(() => import("./pages/Referral"));
const Profile = lazy(() => import("./pages/Profile"));
const Analytics = lazy(() => import("./pages/Analytics"));
const Admin = lazy(() => import("./pages/Admin"));
const AvatarsAdmin = lazy(() => import("./pages/AvatarsAdmin"));
const LocationsAdmin = lazy(() => import("./pages/LocationsAdmin"));
const PromoStudio = lazy(() => import("./pages/PromoStudio"));
const FreeformStudio = lazy(() => import("./pages/FreeformStudio"));
const StoryStudio = lazy(() => import("./pages/StoryStudio"));
const Contact = lazy(() => import("./pages/Contact"));
const Help = lazy(() => import("./pages/Help"));
const FAQ = lazy(() => import("./pages/FAQ"));
const About = lazy(() => import("./pages/About"));
const Blog = lazy(() => import("./pages/Blog"));
const BlogPost = lazy(() => import("./pages/BlogPost"));
const Vitrin = lazy(() => import("./pages/Vitrin"));
const Terms = lazy(() => import("./pages/Terms"));
const Privacy = lazy(() => import("./pages/Privacy"));
const Cookies = lazy(() => import("./pages/Cookies"));
const DistanceSales = lazy(() => import("./pages/DistanceSales"));
const DeliveryReturn = lazy(() => import("./pages/DeliveryReturn"));
const NotFound = lazy(() => import("./pages/NotFound"));
const Integrations = lazy(() => import("./pages/Integrations"));
const AdminAutoBlog = lazy(() => import("./pages/AdminAutoBlog"));
const AdminVideoErrors = lazy(() => import("./pages/AdminVideoErrors"));
const AdminMuhasebe = lazy(() => import("./pages/AdminMuhasebe"));
const AdminPricing = lazy(() => import("./pages/AdminPricing"));
const AdminMail = lazy(() => import("./pages/AdminMail"));
const AdminPaymentLogs = lazy(() => import("./pages/AdminPaymentLogs"));
const AdminPaymentApprovals = lazy(() => import("./pages/AdminPaymentApprovals"));
const AdminAffiliate = lazy(() => import("./pages/AdminAffiliate"));
const AdminLiveChat = lazy(() => import("./pages/AdminLiveChat"));
const TikTokVideoOlusturucu = lazy(() => import("./pages/TikTokVideoOlusturucu"));
const InstagramReelsYapayZeka = lazy(() => import("./pages/InstagramReelsYapayZeka"));
const UgcVideoSatinAl = lazy(() => import("./pages/UgcVideoSatinAl"));
const PolloAlternatifi = lazy(() => import("./pages/PolloAlternatifi"));
const AlternatifSayfasi = lazy(() => import("./pages/AlternatifSayfasi"));
const queryClient = new QueryClient();

// Minimal loading fallback
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
  </div>
);

// Davet linkindeki ?ref=KOD kodunu yakalayıp sakla (kayıtta kullanılır).
const RefCapture = () => {
  React.useEffect(() => {
    const code = new URLSearchParams(window.location.search).get("ref");
    if (code && code.trim()) localStorage.setItem("ref_code", code.trim());
  }, []);
  return null;
};

// Temiz davet linki: /d/KOD → kodu sakla, direkt kayıt sayfasına götür.
const RefRedirect = () => {
  const { code } = useParams();
  if (code && code.trim()) localStorage.setItem("ref_code", code.trim());
  return <Navigate to="/auth?mode=signup" replace />;
};

const App = () => (
  <QueryClientProvider client={queryClient}>
    <AnalyticsProvider gaId="" gtmId="">
      <AuthProvider>
        <NotificationProvider>
          <TooltipProvider>
          <ThemeTransition />
          <Toaster />
          <Sonner />
          <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
            <RefCapture />
            <SnowEffect />
            <NewYearPopup />
            <NotificationCenter />
            <ScrollToTop />
            <ChatWidget />
            <AgreementGate />
            <PwaInstallPrompt />
            <PushNotificationPrompt />
            <MobileBottomNav />
            <Suspense fallback={<PageLoader />}>
              <Routes>
                <Route path="/" element={<Index />} />
                <Route path="/d/:code" element={<RefRedirect />} />
                <Route path="/auth" element={<Auth />} />
                <Route path="/reset-password" element={<ResetPassword />} />
                <Route path="/pricing" element={<Pricing />} />
                <Route path="/contact" element={<Contact />} />
                <Route path="/help" element={<Help />} />
                <Route path="/faq" element={<FAQ />} />
                <Route path="/about" element={<About />} />
                <Route path="/vitrin" element={<Vitrin />} />
                <Route path="/blog" element={<Blog />} />
                <Route path="/blog/:slug" element={<BlogPost />} />
                <Route path="/tiktok-video-olusturucu" element={<TikTokVideoOlusturucu />} />
                <Route path="/instagram-reels-yapay-zeka" element={<InstagramReelsYapayZeka />} />
                <Route path="/ugc-video-satin-al" element={<UgcVideoSatinAl />} />
                <Route path="/pollo-alternatifi" element={<PolloAlternatifi />} />
                <Route path="/heygen-alternatifi" element={<AlternatifSayfasi rakip="heygen" />} />
                <Route path="/synthesia-alternatifi" element={<AlternatifSayfasi rakip="synthesia" />} />
                <Route path="/capcut-alternatifi" element={<AlternatifSayfasi rakip="capcut" />} />
                <Route path="/invideo-alternatifi" element={<AlternatifSayfasi rakip="invideo" />} />
                <Route path="/terms" element={<Terms />} />
                <Route path="/privacy" element={<Privacy />} />
                <Route path="/cookies" element={<Cookies />} />
                <Route path="/distance-sales" element={<DistanceSales />} />
                <Route path="/delivery-return" element={<DeliveryReturn />} />
                <Route path="/results" element={<ProtectedRoute><Results /></ProtectedRoute>} />
                <Route path="/kampanya" element={<ProtectedRoute><Kampanya /></ProtectedRoute>} />
                <Route path="/trend" element={<ProtectedRoute><Trend /></ProtectedRoute>} />
                <Route path="/repurpose" element={<ProtectedRoute><Repurpose /></ProtectedRoute>} />
                <Route path="/sablonlar" element={<ProtectedRoute><Sablonlar /></ProtectedRoute>} />
                <Route path="/influencers" element={<ProtectedRoute><Influencers /></ProtectedRoute>} />
                <Route path="/video-editor" element={<ProtectedRoute><VideoEditor /></ProtectedRoute>} />
                <Route path="/davet" element={<ProtectedRoute><Referral /></ProtectedRoute>} />
                <Route path="/profil" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
                <Route path="/integrations" element={<ProtectedRoute><Integrations /></ProtectedRoute>} />
                <Route path="/serbest-uretim" element={<ProtectedRoute><FreeformStudio /></ProtectedRoute>} />
                <Route path="/hikaye-studyosu" element={<ProtectedRoute><StoryStudio /></ProtectedRoute>} />
                <Route path="/payment-success" element={<PaymentSuccess />} />
                <Route path="/payment-failed" element={<PaymentFailed />} />
                <Route path="/payment-history" element={<ProtectedRoute><PaymentHistory /></ProtectedRoute>} />
                <Route path="/analytics" element={<ProtectedRoute><Analytics /></ProtectedRoute>} />
                <Route path="/admin" element={<AdminRoute><Admin /></AdminRoute>} />
                <Route path="/admin/video-errors" element={<AdminRoute><AdminVideoErrors /></AdminRoute>} />
                <Route path="/admin/muhasebe" element={<AdminRoute><AdminMuhasebe /></AdminRoute>} />
                <Route path="/admin/auto-blog" element={<AdminRoute><AdminAutoBlog /></AdminRoute>} />
                <Route path="/admin/pricing" element={<AdminRoute><AdminPricing /></AdminRoute>} />
                <Route path="/admin/mail" element={<AdminRoute><AdminMail /></AdminRoute>} />
                <Route path="/admin/payment-logs" element={<AdminRoute><AdminPaymentLogs /></AdminRoute>} />
                <Route path="/admin/payment-approvals" element={<AdminRoute><AdminPaymentApprovals /></AdminRoute>} />
                <Route path="/admin/affiliate" element={<AdminRoute><AdminAffiliate /></AdminRoute>} />
                <Route path="/admin/chat" element={<AdminRoute><AdminLiveChat /></AdminRoute>} />
                <Route path="/admin/avatars" element={<AdminRoute><AvatarsAdmin /></AdminRoute>} />
                <Route path="/admin/locations" element={<AdminRoute><LocationsAdmin /></AdminRoute>} />
                <Route path="/admin/promo-studio" element={<AdminRoute><PromoStudio /></AdminRoute>} />
                {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
                <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>
            <ProfileCompletionDialog />
          </BrowserRouter>
        </TooltipProvider>
        </NotificationProvider>
      </AuthProvider>
    </AnalyticsProvider>
  </QueryClientProvider>
);

export default App;
